import * as Effect from "effect/Effect";
import * as Duration from "effect/Duration";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import { ProjectId, TrimmedNonEmptyString, TrimmedString } from "./baseSchemas.ts";
import { ThreadEnvMode } from "./environment.ts";
import { makeProviderSettingsSchema } from "./providerSettingsSchema.ts";
// The provider settings form contract lives in its own leaf so provider
// contract modules import it without a cycle through this file.
export {
  makeProviderSettingsSchema,
  type ProviderSettingsFormAnnotation,
  type ProviderSettingsFormControl,
  type ProviderSettingsFormSchemaAnnotation,
  type ProviderSettingsOrder,
} from "./providerSettingsSchema.ts";
import { DEFAULT_TEXT_GENERATION_REASONING_EFFORT, ProviderOptionSelections } from "./model.ts";
import { DEFAULT_RUNTIME_MODE, ModelSelection, RuntimeMode } from "./orchestration.ts";
import { ProviderInstanceConfig, ProviderInstanceId } from "./providerInstance.ts";
import { ClaudeNativeAccountBinding } from "./claudeAccounts.ts";
import { MusicProviderId } from "./musicProtocol.ts";

// ── Client Settings (local-only) ───────────────────────────────

export const TimestampFormat = Schema.Literals(["locale", "12-hour", "24-hour"]);
export type TimestampFormat = typeof TimestampFormat.Type;
export const DEFAULT_TIMESTAMP_FORMAT: TimestampFormat = "locale";

export const SidebarProjectSortOrder = Schema.Literals(["updated_at", "created_at", "manual"]);
export type SidebarProjectSortOrder = typeof SidebarProjectSortOrder.Type;
export const DEFAULT_SIDEBAR_PROJECT_SORT_ORDER: SidebarProjectSortOrder = "updated_at";

export const SidebarThreadSortOrder = Schema.Literals(["updated_at", "created_at"]);
export type SidebarThreadSortOrder = typeof SidebarThreadSortOrder.Type;
export const DEFAULT_SIDEBAR_THREAD_SORT_ORDER: SidebarThreadSortOrder = "updated_at";

export const SidebarProjectGroupingMode = Schema.Literals([
  "repository",
  "repository_path",
  "separate",
]);
export type SidebarProjectGroupingMode = typeof SidebarProjectGroupingMode.Type;
export const DEFAULT_SIDEBAR_PROJECT_GROUPING_MODE: SidebarProjectGroupingMode = "repository";
export const MIN_SIDEBAR_AUTO_SETTLE_AFTER_DAYS = 1;
export const MAX_SIDEBAR_AUTO_SETTLE_AFTER_DAYS = 90;
export const SidebarAutoSettleAfterDays = Schema.Number.check(
  Schema.isBetween({
    minimum: MIN_SIDEBAR_AUTO_SETTLE_AFTER_DAYS,
    maximum: MAX_SIDEBAR_AUTO_SETTLE_AFTER_DAYS,
  }),
);
export type SidebarAutoSettleAfterDays = typeof SidebarAutoSettleAfterDays.Type;
export const DEFAULT_SIDEBAR_AUTO_SETTLE_AFTER_DAYS: SidebarAutoSettleAfterDays = 3;
export const MIN_GLASS_OPACITY = 40;
export const MAX_GLASS_OPACITY = 100;
export const GlassOpacity = Schema.Int.check(
  Schema.isBetween({
    minimum: MIN_GLASS_OPACITY,
    maximum: MAX_GLASS_OPACITY,
  }),
);
export type GlassOpacity = typeof GlassOpacity.Type;
export const DEFAULT_GLASS_OPACITY: GlassOpacity = 80;
/**
 * Font size preferences, in CSS pixels. The ranges are deliberately narrow:
 * the interface size scales every rem-based dimension in the app, so the
 * bounds keep layouts intact rather than offering unusable extremes.
 */
export const MIN_INTERFACE_FONT_SIZE = 12;
export const MAX_INTERFACE_FONT_SIZE = 20;
export const InterfaceFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_INTERFACE_FONT_SIZE, maximum: MAX_INTERFACE_FONT_SIZE }),
);
export type InterfaceFontSize = typeof InterfaceFontSize.Type;
export const DEFAULT_INTERFACE_FONT_SIZE: InterfaceFontSize = 16;

export const MIN_PROMPT_FONT_SIZE = 12;
export const MAX_PROMPT_FONT_SIZE = 20;
export const PromptFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_PROMPT_FONT_SIZE, maximum: MAX_PROMPT_FONT_SIZE }),
);
export type PromptFontSize = typeof PromptFontSize.Type;
export const DEFAULT_PROMPT_FONT_SIZE: PromptFontSize = 14;

export const MIN_CODE_FONT_SIZE = 10;
export const MAX_CODE_FONT_SIZE = 18;
export const CodeFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_CODE_FONT_SIZE, maximum: MAX_CODE_FONT_SIZE }),
);
export type CodeFontSize = typeof CodeFontSize.Type;
export const DEFAULT_CODE_FONT_SIZE: CodeFontSize = 13;

export const MIN_TERMINAL_FONT_SIZE = 8;
export const MAX_TERMINAL_FONT_SIZE = 20;
export const TerminalFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_TERMINAL_FONT_SIZE, maximum: MAX_TERMINAL_FONT_SIZE }),
);
export type TerminalFontSize = typeof TerminalFontSize.Type;
export const DEFAULT_TERMINAL_FONT_SIZE: TerminalFontSize = 12;

export const EnvironmentIdentificationMode = Schema.Literals(["artwork", "pill", "none"]);
export type EnvironmentIdentificationMode = typeof EnvironmentIdentificationMode.Type;
export const DEFAULT_ENVIRONMENT_IDENTIFICATION_MODE: EnvironmentIdentificationMode = "artwork";

// RL-CAN-12: this is the persisted wire representation of
// @neucleos/theme's existing UserThemeDensity vocabulary. The transport
// contract stays dependency-downstream-neutral; the web shell translates
// this structurally identical union into the theme package's named type.
export const ThemeDensity = Schema.Literals(["comfortable", "compact"]);
export type ThemeDensity = typeof ThemeDensity.Type;
export const DEFAULT_THEME_DENSITY: ThemeDensity = "comfortable";

// HIG3 (GOALS/GOALS-DESIGN.html#hig3): the person's contrast choice, mirroring
// @neucleos/theme's user vocabulary (standard | high) plus "system", which
// follows the OS Increase Contrast setting. The root wears data-contrast="high"
// while the resolved choice is high.
export const ThemeContrast = Schema.Literals(["system", "standard", "high"]);
export type ThemeContrast = typeof ThemeContrast.Type;
export const DEFAULT_THEME_CONTRAST: ThemeContrast = "system";

// ST10 (GOALS-SETTINGS.html#st10): what shows while Cleo works, the person's
// choice. The loop and loader literals mirror the vendored MetalForge
// exports (ThinkingOrbsPill's ORB_STYLES, Loader's LOADER_NAMES) and are
// pinned to those bytes by story in apps/web (workingIndicator.test.ts).
export const WorkingIndicatorKind = Schema.Literals(["orb", "loader", "dots"]);
export type WorkingIndicatorKind = typeof WorkingIndicatorKind.Type;
export const WorkingIndicatorOrbLoop = Schema.Literals([
  "breathe",
  "rings",
  "twinkle",
  "vortex",
  "band",
  "popcorn",
  "converge",
  "nest",
  "sag",
  "terminator",
  "stripes",
  "pinch",
  "rock",
  "spinup",
  "spot",
  "noise",
  "nested",
  "burst",
  "wave",
  "coil",
  "grid",
  "gyro",
  "drain",
  "tide",
  "cube",
  "ratchet",
  "funnel",
  "magnet",
  "layers",
  "ribbon",
  "rain",
  "ping",
  "arms",
  "crystal",
  "wedge",
  "bounce",
  "gimbal",
  "chase",
]);
export type WorkingIndicatorOrbLoop = typeof WorkingIndicatorOrbLoop.Type;
export const WorkingIndicatorLoader = Schema.Literals([
  "tide",
  "fountain",
  "chomp",
  "pogo",
  "footsteps",
  "compass",
  "yo-yo",
  "cradle",
  "constellation",
  "riser",
  "crosstalk",
  "belt",
  "ratchet",
  "swell",
  "hourglass",
  "tally",
  "fan",
  "dial",
  "skip",
  "dominoes",
  "vitals",
  "spark",
  "spiral",
  "lattice",
  "rainfall",
  "sonar",
  "signal",
  "metronome",
  "cascade",
  "portal",
  "orbit",
  "infinity",
  "hop",
  "abacus",
  "hammer",
  "plinko",
  "elevator",
]);
export type WorkingIndicatorLoader = typeof WorkingIndicatorLoader.Type;

/**
 * A user-chosen font family (a single name or a comma-separated list). Empty
 * means "use the app default"; clients compose their own fallback stacks.
 */
export const FontFamilyPreference = Schema.String.check(Schema.isMaxLength(200));
export type FontFamilyPreference = typeof FontFamilyPreference.Type;

/**
 * FD26 (register FD-CAN-26): the shell's panel frost — "frosted" keeps the
 * translucent backdrop blur; "opaque" turns panels into honest solid
 * surface tokens (the same shape forced-colors takes).
 */
export const ShellGlass = Schema.Literals(["frosted", "opaque"]);
export type ShellGlass = typeof ShellGlass.Type;

/**
 * UP6 (GOALS/GOALS-UPSTREAM-PARITY.html#UP6): how a background session's
 * completion, failure, input request or approval request reaches the person.
 * Every channel is opt-in and the resting state is "off": nothing fires by
 * default, nothing replays history (initial and reconnect snapshots never
 * notify), the active session never notifies. Device-local: this Mac's
 * choice, never the World's. Upstream references #11481, #11570, #11569.
 */
export const SessionNotificationMode = Schema.Literals([
  "off",
  "system",
  "sound",
  "system-and-sound",
]);
export type SessionNotificationMode = typeof SessionNotificationMode.Type;
export const DEFAULT_SESSION_NOTIFICATION_MODE: SessionNotificationMode = "off";
/** The bundled CC0 sounds under apps/web/public/sounds (LICENSE beside them). */
export const SessionNotificationSound = Schema.Literals(["soft-chime", "two-tone"]);
export type SessionNotificationSound = typeof SessionNotificationSound.Type;
export const DEFAULT_SESSION_NOTIFICATION_SOUND: SessionNotificationSound = "soft-chime";

/**
 * UP5 (GOALS/GOALS-UPSTREAM-PARITY.html#UP5): how assistant text reaches the
 * projection while a turn runs. `turn` waits for the whole message,
 * `paragraph` paints each finished paragraph or closed code block (default),
 * `token` forwards every provider delta (legacy; the Desk warns). A server
 * setting because the ingestion layer applies it. Read by
 * apps/server/src/orchestration/Layers/ProviderRuntimeIngestion.ts.
 */
export const ResponseStreamingMode = Schema.Literals(["turn", "paragraph", "token"]);
export type ResponseStreamingMode = typeof ResponseStreamingMode.Type;
export const DEFAULT_RESPONSE_STREAMING_MODE: ResponseStreamingMode = "paragraph";

export const ClientSettingsSchema = Schema.Struct({
  // FD26: off rests the shell's aurora drift (and every future ambient
  // recipe) even where the OS allows motion; reduced-motion always wins.
  lightStudy: Schema.Literals([
    "none",
    "downlight",
    "pool",
    "rim",
    "wash",
    "ambient",
    "combined",
  ]).pipe(Schema.withDecodingDefault(Effect.succeed("combined"))),
  lightIntensity: Schema.Literals(["whisper", "quiet", "present"]).pipe(
    Schema.withDecodingDefault(Effect.succeed("quiet")),
  ),
  // HIG4 (GOALS/GOALS-DESIGN.html#hig4, dated 2026-09-14): the shell is quiet
  // by default — breathe off, ambient motion off, frosted panels off, tone-down
  // on. Every knob stays; only the resting state changed.
  lightBreathe: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  hoverLight: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  opaqueDoors: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  doorFade: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  cardGlow: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 100 })).pipe(
    Schema.withDecodingDefault(Effect.succeed(100)),
  ),
  ambientMotion: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  workingIndicator: WorkingIndicatorKind.pipe(Schema.withDecodingDefault(Effect.succeed("orb"))),
  workingIndicatorOrbLoop: WorkingIndicatorOrbLoop.pipe(
    Schema.withDecodingDefault(Effect.succeed("breathe")),
  ),
  workingIndicatorLoader: WorkingIndicatorLoader.pipe(
    Schema.withDecodingDefault(Effect.succeed("tide")),
  ),
  confirmThreadArchive: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  confirmThreadDelete: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  diffIgnoreWhitespace: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  density: ThemeDensity.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THEME_DENSITY))),
  contrast: ThemeContrast.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THEME_CONTRAST))),
  environmentIdentificationMode: EnvironmentIdentificationMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_ENVIRONMENT_IDENTIFICATION_MODE)),
  ),
  glassOpacity: GlassOpacity.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_GLASS_OPACITY)),
  ),
  fontSizeInterface: InterfaceFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_INTERFACE_FONT_SIZE)),
  ),
  fontSizePrompt: PromptFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROMPT_FONT_SIZE)),
  ),
  fontSizeCode: CodeFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_CODE_FONT_SIZE)),
  ),
  fontSizeTerminal: TerminalFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_TERMINAL_FONT_SIZE)),
  ),
  fontFamilyCode: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyComposer: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilySans: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyTerminal: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  // Grayscale `-webkit-font-smoothing: antialiased` (thinner strokes);
  // disabling restores the platform's heavier default. No effect off macOS.
  fontSmoothing: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  // Model favorites. Historically keyed by provider kind, now
  // widened to `ProviderInstanceId` so users can favorite a specific model
  // on a custom provider instance (e.g. "Codex Personal · gpt-5") without
  // the UI collapsing it into the same bucket as the default Codex. The
  // widening is backward-compatible by construction: prior provider-kind
  // strings satisfy the `ProviderInstanceId` slug schema, so previously
  // persisted favorites decode unchanged and continue to point at the
  // default instance for their kind (because `defaultInstanceIdForDriver(kind)`
  // uses the same slug). The field name is kept as `provider` for storage
  // stability; new call sites should treat the value as an instance id.
  favorites: Schema.Array(
    Schema.Struct({
      provider: ProviderInstanceId,
      model: TrimmedNonEmptyString,
    }),
  ).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
  providerModelPreferences: Schema.Record(
    ProviderInstanceId,
    Schema.Struct({
      hiddenModels: Schema.Array(Schema.String).pipe(
        Schema.withDecodingDefault(Effect.succeed([])),
      ),
      modelOrder: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
    }),
  ).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  // Legacy plan mode. The composer's Build/Plan toggle was removed from the
  // default UI; this beta flag restores it (plus the /plan and /default slash
  // commands) for users who still rely on the old workflow.
  planModeEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  // "Queue messages while Cleo works". On: a Send while a response runs waits
  // in the conversation as Unread. Off: that Send goes to her now, as a steer
  // or a stop-and-send, whichever the engine declares (`midRunDelivery`).
  queueMessagesWhileWorking: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  // Widen Cleo (dated 2026-10-07): the Cleo Panel asks for twice her old
  // width, folding the context rail and then the dock for it; off re-derives
  // the layout. One switch for the panel's button, the View menu and Cleo's
  // own set_setting, per Mac.
  cleoPanelWide: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  sidebarAutoSettleAfterDays: Schema.NullOr(SidebarAutoSettleAfterDays).pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_AUTO_SETTLE_AFTER_DAYS)),
  ),
  sidebarProjectGroupingMode: SidebarProjectGroupingMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_PROJECT_GROUPING_MODE)),
  ),
  sidebarProjectGroupingOverrides: Schema.Record(
    TrimmedNonEmptyString,
    SidebarProjectGroupingMode,
  ).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  sidebarProjectSortOrder: SidebarProjectSortOrder.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_PROJECT_SORT_ORDER)),
  ),
  sidebarThreadSortOrder: SidebarThreadSortOrder.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_THREAD_SORT_ORDER)),
  ),
  // UP6: session notifications, each opt-in; off notifies nothing.
  sessionNotifications: SessionNotificationMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SESSION_NOTIFICATION_MODE)),
  ),
  sessionNotificationsInApp: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  sessionNotificationBadge: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  sessionNotificationSound: SessionNotificationSound.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SESSION_NOTIFICATION_SOUND)),
  ),
  shellGlass: ShellGlass.pipe(Schema.withDecodingDefault(Effect.succeed("opaque"))),
  timestampFormat: TimestampFormat.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_TIMESTAMP_FORMAT)),
  ),
  // FD26: the staked Founders "tone down" — every section wears its group
  // lead's color, computed at paint time (collapseSectionAccents), never
  // stored collapsed.
  toneDownSections: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  wordWrap: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
});
export type ClientSettings = typeof ClientSettingsSchema.Type;

export const DEFAULT_CLIENT_SETTINGS: ClientSettings = Schema.decodeSync(ClientSettingsSchema)({});

// ── Server Settings (server-authoritative) ────────────────────

// Moved to environment.ts so orchestration contracts can use it without an
// import cycle; re-exported here for compatibility with deep imports.
export { ThreadEnvMode } from "./environment.ts";

const makeBinaryPathSetting = (fallback: string) =>
  TrimmedString.pipe(
    Schema.decodeTo(
      Schema.String,
      SchemaTransformation.transformOrFail({
        decode: (value) => Effect.succeed(value || fallback),
        encode: (value) => Effect.succeed(value),
      }),
    ),
    Schema.withDecodingDefault(Effect.succeed(fallback)),
  );

export const CodexSettings = makeProviderSettingsSchema(
  {
    chatgptPlan: Schema.optionalKey(Schema.Boolean).pipe(
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    managedExecution: Schema.optionalKey(
      Schema.Struct({
        // Marks the instance managed; the running policy is the server's
        // (apps/server/src/neucleos/managedCodexPolicy.ts), not these numbers.
        maxOutputTokens: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
        maximumAmountMicros: Schema.Int.check(
          Schema.isBetween({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
        ),
      }),
    ).pipe(Schema.annotateKey({ providerSettingsForm: { hidden: true } })),
    enabled: Schema.Boolean.pipe(
      Schema.withDecodingDefault(Effect.succeed(true)),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    binaryPath: makeBinaryPathSetting("codex").pipe(
      Schema.annotateKey({
        title: "Binary path",
        description: "Path to the Codex binary used by this instance.",
        providerSettingsForm: { placeholder: "codex", clearWhenEmpty: "omit" },
      }),
    ),
    homePath: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "CODEX_HOME path",
        description: "Custom Codex home and config directory.",
        providerSettingsForm: {
          placeholder: "~/.codex",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    shadowHomePath: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Shadow home path",
        description:
          "Account-specific Codex home. Keeps auth.json separate while sharing state from CODEX_HOME.",
        providerSettingsForm: {
          placeholder: "~/.codex-cleo/personal",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    launchArgs: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Launch arguments",
        description: "Additional CLI arguments passed to codex app-server on session start.",
      }),
    ),
    customModels: Schema.Array(Schema.String).pipe(
      Schema.withDecodingDefault(Effect.succeed([])),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
  },
  {
    order: ["binaryPath", "homePath", "shadowHomePath", "launchArgs"],
  },
);
export type CodexSettings = typeof CodexSettings.Type;

/**
 * UP1 (GOALS/GOALS-UPSTREAM-PARITY.html#UP1): the context size, in tokens, at
 * which a Claude session compacts its own history. Absent means Claude's own
 * threshold; a present value reaches the engine as
 * CLAUDE_CODE_AUTO_COMPACT_WINDOW (ClaudeAdapter.readClaudeAutoCompactWindow).
 * Clearing the field in the provider's settings removes it, so the engine's
 * default returns.
 */
export const CLAUDE_AUTO_COMPACT_WINDOW_MIN = 100_000;
export const CLAUDE_AUTO_COMPACT_WINDOW_MAX = 1_000_000;
export const ClaudeAutoCompactWindow = Schema.Int.check(
  Schema.isBetween({
    minimum: CLAUDE_AUTO_COMPACT_WINDOW_MIN,
    maximum: CLAUDE_AUTO_COMPACT_WINDOW_MAX,
  }),
);

export const ClaudeSettings = makeProviderSettingsSchema(
  {
    nativeAccount: Schema.optionalKey(ClaudeNativeAccountBinding).pipe(
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    enabled: Schema.Boolean.pipe(
      Schema.withDecodingDefault(Effect.succeed(true)),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    binaryPath: makeBinaryPathSetting("claude").pipe(
      Schema.annotateKey({
        title: "Binary path",
        description: "Path to the Claude binary used by this instance.",
        providerSettingsForm: { placeholder: "claude", clearWhenEmpty: "omit" },
      }),
    ),
    homePath: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "CLAUDE_CONFIG_DIR path",
        description:
          "Custom Claude home and config directory. Keeps .claude.json and .claude separate.",
        providerSettingsForm: { placeholder: "~/.claude", clearWhenEmpty: "omit" },
      }),
    ),
    customModels: Schema.Array(Schema.String).pipe(
      Schema.withDecodingDefault(Effect.succeed([])),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    launchArgs: Schema.String.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Launch arguments",
        description: "Additional CLI arguments passed on session start.",
        providerSettingsForm: {
          placeholder: "e.g. --chrome",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    autoCompactWindow: Schema.optionalKey(ClaudeAutoCompactWindow).pipe(
      Schema.annotateKey({
        title: "Auto-compact window (advanced)",
        description:
          "Context size in tokens at which this instance's sessions compact their history: 100,000 to 1,000,000. Leave empty for Claude's own threshold.",
        providerSettingsForm: {
          control: "number",
          placeholder: "Claude's own threshold",
          clearWhenEmpty: "omit",
          min: CLAUDE_AUTO_COMPACT_WINDOW_MIN,
          max: CLAUDE_AUTO_COMPACT_WINDOW_MAX,
          step: 1_000,
        },
      }),
    ),
  },
  {
    order: ["binaryPath", "homePath", "launchArgs", "autoCompactWindow"],
  },
);
export type ClaudeSettings = typeof ClaudeSettings.Type;

/**
 * Open Interpreter driver settings (OI2, D11 lane). Deliberately narrower
 * than the other drivers: there is NO `binaryPath` field — the binary is
 * always resolved from the provisioned home (`<oiHomePath>/package/bin/…`),
 * and the driver refuses at create() unless the home's `provenance.json`
 * matches the pinned, checksummed release. A free binary path would be a
 * door around that custody gate. API-key/local-endpoint only; disabled by
 * default and never surfaced without an explicit `providerInstances` entry
 * (no legacy `providers.<kind>` mirror exists for this driver).
 */
export const OpenInterpreterSettings = makeProviderSettingsSchema(
  {
    enabled: Schema.Boolean.pipe(
      Schema.withDecodingDefault(Effect.succeed(false)),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    oiHomePath: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Provisioned OI home path",
        description:
          "Isolated home holding the pinned Open Interpreter release and its provenance.json (provisioned by scripts/provision-open-interpreter.mjs).",
        providerSettingsForm: {
          placeholder: "~/Neucleos-oi-home",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    customModels: Schema.Array(Schema.String).pipe(
      Schema.withDecodingDefault(Effect.succeed([])),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
  },
  {
    order: ["oiHomePath"],
  },
);
export type OpenInterpreterSettings = typeof OpenInterpreterSettings.Type;

export const ObservabilitySettings = Schema.Struct({
  otlpTracesUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  otlpMetricsUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
});
export type ObservabilitySettings = typeof ObservabilitySettings.Type;

export const SourceControlWritingStyleMode = Schema.Literals([
  "repo_conventions",
  "conventional_commits",
  "custom",
]);
export type SourceControlWritingStyleMode = typeof SourceControlWritingStyleMode.Type;

export const SourceControlWritingStyleSettings = Schema.Struct({
  mode: SourceControlWritingStyleMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("repo_conventions" as const)),
  ),
  customInstructions: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  followChangeRequestTemplates: Schema.Boolean.pipe(
    Schema.withDecodingDefault(Effect.succeed(true)),
  ),
});
export type SourceControlWritingStyleSettings = typeof SourceControlWritingStyleSettings.Type;

export const DEFAULT_AUTOMATIC_GIT_FETCH_INTERVAL = Duration.seconds(30);
export const DEFAULT_PROVIDER_HEALTH_REFRESH_INTERVAL = Duration.minutes(5);

export const BackgroundActivityProfile = Schema.Literals([
  "balanced",
  "performance",
  "battery-saver",
]);
export type BackgroundActivityProfile = typeof BackgroundActivityProfile.Type;
export const DEFAULT_BACKGROUND_ACTIVITY_PROFILE: BackgroundActivityProfile = "balanced";

export const BackgroundActivityProfileSelection = Schema.Literals([
  "balanced",
  "performance",
  "battery-saver",
  "custom",
]);
export type BackgroundActivityProfileSelection = typeof BackgroundActivityProfileSelection.Type;

export const BackgroundActivityOverrides = Schema.Struct({
  automaticGitFetchInterval: Schema.optionalKey(Schema.DurationFromMillis),
  providerHealthRefreshInterval: Schema.optionalKey(Schema.DurationFromMillis),
  hostPowerMonitorActiveInterval: Schema.optionalKey(Schema.DurationFromMillis),
  hostPowerMonitorIdleInterval: Schema.optionalKey(Schema.DurationFromMillis),
  idleClientTtl: Schema.optionalKey(Schema.DurationFromMillis),
  pauseWhenHostLocked: Schema.optionalKey(Schema.Boolean),
  pauseWhenHostLowPower: Schema.optionalKey(Schema.Boolean),
  pauseWhenClientLowPower: Schema.optionalKey(Schema.Boolean),
  pauseWhenOnBattery: Schema.optionalKey(Schema.Boolean),
});
export type BackgroundActivityOverrides = typeof BackgroundActivityOverrides.Type;

export const BackgroundActivitySettings = Schema.Struct({
  schemaVersion: Schema.Literal(1).pipe(Schema.withDecodingDefault(Effect.succeed(1 as const))),
  profile: BackgroundActivityProfileSelection.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BACKGROUND_ACTIVITY_PROFILE)),
  ),
  baseProfile: Schema.optionalKey(BackgroundActivityProfile),
  overrides: BackgroundActivityOverrides.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
}).pipe(Schema.withDecodingDefault(Effect.succeed({})));
export type BackgroundActivitySettings = typeof BackgroundActivitySettings.Type;

/**
 * One World's device-local workspace binding (principal rulings 2026-08-28:
 * repository custody is unhooked, and the folder LIST is the model — "add as
 * many folders as they want … including a code folder, and it's all one"):
 *
 * - `folders` — the ordered list of local folders that are this World's
 *   file-system defaults on THIS machine, code or not. A DEFINED list is the
 *   exclusive authority (an explicitly emptied list means unbound — never a
 *   legacy fallback); an undefined list keeps the legacy fields below
 *   honored so existing setups work untouched. World-parented root sessions
 *   start in `folders[0]`; git-ness is DETECTED per folder, never declared.
 *   Absolute paths are legal here and exactly here, because environment-local
 *   server settings never reach Convex (O12). A patched `folders` array
 *   replaces wholesale (see `applyServerSettingsPatch`), never index-merges.
 * - `defaultExecutionProjectId` — LEGACY: the pre-folder-list bound project.
 *   Honored only while `folders` is undefined.
 * - `filesRoot` — LEGACY as a binding (honored only while `folders` is
 *   undefined), but still the write target of the "Use default" ceremony
 *   (the server expands `~` and creates the folder on this field's writes).
 */
// Decode the previous single-path settings without losing their choices.
// New writes always encode lists, including [] for a deliberately empty list.
const WorldSourceFolderList = Schema.Union([
  Schema.Array(TrimmedNonEmptyString),
  Schema.NullOr(TrimmedNonEmptyString),
]).pipe(
  Schema.decodeTo(
    Schema.Array(TrimmedNonEmptyString),
    SchemaTransformation.transformOrFail({
      decode: (value) =>
        Effect.succeed(value === null ? [] : typeof value === "string" ? [value] : value),
      encode: (value) => Effect.succeed(value),
    }),
  ),
);

/** WB6 (principal ruling 2026-09-10): what the person calls a listed folder
 * and what they say it holds, stored beside the folder's path on this Mac —
 * never inside the folder, never in the World authority. Keyed by the saved
 * path; the server ceremony drops a detail whose path leaves the list.
 * Whitespace is absence. The description is never injected: it rides the
 * folder's reference into the rail, the birth references and Cleo's listing. */
export const WORLD_SOURCE_FOLDER_NAME_MAX_CHARS = 120;
export const WORLD_SOURCE_FOLDER_DESCRIPTION_MAX_CHARS = 4_000;
export const WorldSourceFolderDetail = Schema.Struct({
  name: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(WORLD_SOURCE_FOLDER_NAME_MAX_CHARS)),
  ),
  description: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(WORLD_SOURCE_FOLDER_DESCRIPTION_MAX_CHARS)),
  ),
});
export type WorldSourceFolderDetail = typeof WorldSourceFolderDetail.Type;

/** WB5: independently optional lists of explicitly chosen folders on this
 * Mac. New sessions receive these folder references; nothing is inferred.
 * WB6: `details` carries each listed path's person-given name and description. */
export const WorldSourceFolders = Schema.Struct({
  codebase: WorldSourceFolderList,
  files: WorldSourceFolderList,
  details: Schema.optional(Schema.Record(TrimmedNonEmptyString, WorldSourceFolderDetail)),
});
export type WorldSourceFolders = typeof WorldSourceFolders.Type;

export const WorldWorkspaceBinding = Schema.Struct({
  folders: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  defaultExecutionProjectId: Schema.optional(ProjectId),
  filesRoot: Schema.optional(TrimmedNonEmptyString),
  sourceFolders: Schema.optional(WorldSourceFolders),
});
export type WorldWorkspaceBinding = typeof WorldWorkspaceBinding.Type;

// ── Scoped settings (UP7) ─────────────────────────────────────
//
// One override record and one list of scopable keys. A scopable key resolves
// World default → room override → session (GOALS/GOALS-UPSTREAM-PARITY.html#UP7):
// the session's own explicit choice wins, else the room's override, else the
// World's override, else this Mac's plain setting. Reset removes the override
// row and the value above it returns — inheritance is restored, never a copy
// written down. Scopes are World and room, the Cleo nouns (GOALS-ROOMS.html);
// there is no "environment" or "project" scope. The rows live in the
// device-local server settings beside `worldWorkspaceBindings`, keyed by
// worldKey and roomId; a session never stores a resolved copy.

/** Where an override applies: a World, or one room of a World. */
export const SettingScope = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("world"), worldKey: TrimmedNonEmptyString }),
  Schema.Struct({
    kind: Schema.Literal("room"),
    worldKey: TrimmedNonEmptyString,
    roomId: TrimmedNonEmptyString,
  }),
]);
export type SettingScope = typeof SettingScope.Type;

/**
 * The one override record: a scope, a scopable key and that key's own value
 * type. Each member's `value` is the same schema the plain setting uses, so a
 * refusal names the contract's words and a row never carries a value the
 * setting itself would refuse.
 */
export const ScopedSettingOverride = Schema.Union([
  Schema.Struct({
    scope: SettingScope,
    key: Schema.Literal("defaultSessionModel"),
    value: Schema.NullOr(ModelSelection),
  }),
  Schema.Struct({
    scope: SettingScope,
    key: Schema.Literal("defaultRuntimeMode"),
    value: RuntimeMode,
  }),
  Schema.Struct({
    scope: SettingScope,
    key: Schema.Literal("defaultThreadEnvMode"),
    value: ThreadEnvMode,
  }),
  // UP5: a World or a room may paint replies differently from this Mac.
  Schema.Struct({
    scope: SettingScope,
    key: Schema.Literal("responseStreamingMode"),
    value: ResponseStreamingMode,
  }),
]);
export type ScopedSettingOverride = typeof ScopedSettingOverride.Type;

/** The one list of scopable keys, derived from the override record's members. */
export const SCOPABLE_SETTING_KEYS = [
  "defaultSessionModel",
  "defaultRuntimeMode",
  "defaultThreadEnvMode",
  "responseStreamingMode",
] as const satisfies ReadonlyArray<ScopedSettingOverride["key"]>;
export type ScopableSettingKey = (typeof SCOPABLE_SETTING_KEYS)[number];
export const ScopableSettingKey = Schema.Literals(SCOPABLE_SETTING_KEYS);
export const isScopableSettingKey = (key: string): key is ScopableSettingKey =>
  (SCOPABLE_SETTING_KEYS as ReadonlyArray<string>).includes(key);

/** The plain settings the scopable keys read when no override row applies. */
export interface ScopableSettingValues {
  readonly defaultSessionModel: ModelSelection | null;
  readonly defaultRuntimeMode: RuntimeMode;
  readonly defaultThreadEnvMode: ThreadEnvMode;
  readonly responseStreamingMode: ResponseStreamingMode;
}
/** The value type one scopable key carries. */
export type ScopableSettingValue<K extends ScopableSettingKey> = ScopableSettingValues[K];

/** What a resolution reads: the plain values and the override record. */
export interface ScopedSettingsSource extends ScopableSettingValues {
  readonly scopedSettingOverrides: ReadonlyArray<ScopedSettingOverride>;
}

/** Names an override row: the reset operation removes exactly this row. */
export const ScopedSettingOverrideRef = Schema.Struct({
  scope: SettingScope,
  key: ScopableSettingKey,
});
export type ScopedSettingOverrideRef = typeof ScopedSettingOverrideRef.Type;

export const sameSettingScope = (a: SettingScope, b: SettingScope): boolean =>
  a.kind === b.kind &&
  a.worldKey === b.worldKey &&
  (a.kind === "world" || b.kind === "world" || a.roomId === b.roomId);

export const sameScopedSettingOverrideRef = (
  a: ScopedSettingOverrideRef,
  b: ScopedSettingOverrideRef,
): boolean => a.key === b.key && sameSettingScope(a.scope, b.scope);

/** The layer a resolved value came from, innermost first. */
export type ScopedSettingSource = "session" | "room" | "world" | "default";

export interface ResolvedScopedSetting<K extends ScopableSettingKey> {
  readonly key: K;
  readonly value: ScopableSettingValue<K>;
  readonly source: ScopedSettingSource;
}

/** The coordinate a resolution reads at: the World, and the room when known. */
export interface SettingScopeCoordinate {
  readonly worldKey: string;
  readonly roomId?: string | null | undefined;
}

export function findScopedSettingOverride<K extends ScopableSettingKey>(
  overrides: ReadonlyArray<ScopedSettingOverride>,
  ref: { readonly scope: SettingScope; readonly key: K },
):
  | { readonly scope: SettingScope; readonly key: K; readonly value: ScopableSettingValue<K> }
  | undefined {
  return overrides.find((row) => sameScopedSettingOverrideRef(row, ref)) as
    | { readonly scope: SettingScope; readonly key: K; readonly value: ScopableSettingValue<K> }
    | undefined;
}

/**
 * Resolve one scopable key at a coordinate: session (the caller's explicit
 * choice, when given) → room override → World override → the plain setting.
 * `explicit === undefined` means the session made no choice; a session's
 * explicit `null` for the model IS a choice (Neucleos Fast).
 */
export function resolveScopedSetting<K extends ScopableSettingKey>(
  settings: ScopedSettingsSource,
  key: K,
  at: SettingScopeCoordinate,
  explicit?: ScopableSettingValue<K>,
): ResolvedScopedSetting<K> {
  if (explicit !== undefined) return { key, value: explicit, source: "session" };
  const roomId = at.roomId ?? null;
  if (roomId !== null) {
    const room = findScopedSettingOverride(settings.scopedSettingOverrides, {
      scope: { kind: "room", worldKey: at.worldKey, roomId },
      key,
    });
    if (room !== undefined) return { key, value: room.value, source: "room" };
  }
  const world = findScopedSettingOverride(settings.scopedSettingOverrides, {
    scope: { kind: "world", worldKey: at.worldKey },
    key,
  });
  if (world !== undefined) return { key, value: world.value, source: "world" };
  return { key, value: settings[key] as ScopableSettingValue<K>, source: "default" };
}

/** Every scopable key resolved at one coordinate — the resolved settings door's payload. */
export function resolveScopedSettings(
  settings: ScopedSettingsSource,
  at: SettingScopeCoordinate,
): { readonly [K in ScopableSettingKey]: ResolvedScopedSetting<K> } {
  return {
    defaultSessionModel: resolveScopedSetting(settings, "defaultSessionModel", at),
    defaultRuntimeMode: resolveScopedSetting(settings, "defaultRuntimeMode", at),
    defaultThreadEnvMode: resolveScopedSetting(settings, "defaultThreadEnvMode", at),
    responseStreamingMode: resolveScopedSetting(settings, "responseStreamingMode", at),
  };
}

/** Upsert by (scope, key): the override record has one row per identity. */
export function upsertScopedSettingOverrides(
  current: ReadonlyArray<ScopedSettingOverride>,
  rows: ReadonlyArray<ScopedSettingOverride>,
): ReadonlyArray<ScopedSettingOverride> {
  const next = [...current];
  for (const row of rows) {
    const index = next.findIndex((existing) => sameScopedSettingOverrideRef(existing, row));
    if (index === -1) next.push(row);
    else next[index] = row;
  }
  return next;
}

/** Reset: remove exactly the named rows; the layer above returns on its own. */
export function resetScopedSettingOverrides(
  current: ReadonlyArray<ScopedSettingOverride>,
  refs: ReadonlyArray<ScopedSettingOverrideRef>,
): ReadonlyArray<ScopedSettingOverride> {
  return current.filter((row) => !refs.some((ref) => sameScopedSettingOverrideRef(row, ref)));
}

/**
 * The write-side cap the World authority enforces on one instruction layer
 * (`neucleos_save_instructions` refuses longer text): a layer is boot
 * doctrine (orientation and pointers), not a document store. Named here so
 * the Settings editor and the server refuse at the same number the authority
 * does. Instruction text itself is never held in these settings — the
 * authority owns every layer (2026-09-09, "it saves it in Convex, not local").
 */
export const SESSION_INJECTION_LAYER_MAX_CHARS = 32_000;

export const ServerSettings = Schema.Struct({
  // The model a new room's first session starts on when the person has not
  // chosen one (2026-09-10): null means Neucleos Fast. Changeable in the
  // session screen; this only names the default.
  defaultSessionModel: Schema.NullOr(ModelSelection).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  // The model background text runs on — room and thread titles, summaries,
  // source-control prose. Null formats automatic names from the message without
  // inference; other explicitly requested background generation defaults to Fast.
  textGenerationModelSelection: Schema.NullOr(ModelSelection).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  // Legacy preference retained for saved-settings compatibility only.
  // It never grants tools: a session's set comes from its room board's declared
  // tools and the person's explicit choice.
  defaultSessionTools: Schema.Array(TrimmedNonEmptyString).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  // Device-local choice of the one process-global Music provider. Null is an
  // honest unchosen state; authorization and computed capabilities live in
  // the selected adapter, never in settings.
  musicProvider: Schema.NullOr(MusicProviderId).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  // MUS6 (register MUS-CAN-06, 2026-09-05): the Spotify application this Mac
  // signs in under. Null means Neucleos's own application (the engine's
  // default); a person past Spotify's five-user Development Mode cap pastes
  // their own application's client id. A public PKCE identifier, never a
  // secret — it names the application, it grants nothing.
  spotifyClientId: Schema.NullOr(TrimmedNonEmptyString).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  enableProviderUpdateChecks: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  // ST4 (register ST-CAN-04): whether anonymous product analytics may leave
  // this machine. A server setting because the analytics service runs in the
  // server; the environment variable CLEO_TELEMETRY_ENABLED=false still wins
  // as a hard floor (an operator's kill switch), this is the person's choice.
  telemetryEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  backgroundActivity: BackgroundActivitySettings,
  // Legacy flat fields retained for old settings files and old clients. New
  // consumers should resolve `backgroundActivity` instead.
  automaticGitFetchInterval: Schema.DurationFromMillis.pipe(
    Schema.withDecodingDefault(
      Effect.succeed(Duration.toMillis(DEFAULT_AUTOMATIC_GIT_FETCH_INTERVAL)),
    ),
  ),
  providerHealthRefreshInterval: Schema.DurationFromMillis.pipe(
    Schema.withDecodingDefault(
      Effect.succeed(Duration.toMillis(DEFAULT_PROVIDER_HEALTH_REFRESH_INTERVAL)),
    ),
  ),
  backgroundActivityProfile: BackgroundActivityProfile.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BACKGROUND_ACTIVITY_PROFILE)),
  ),
  defaultThreadEnvMode: ThreadEnvMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("local" as const satisfies ThreadEnvMode)),
  ),
  // UP7 (#11346 as reference): the permission mode a NEW session starts in
  // when neither the person's draft nor a room or World override chose one.
  // Existing sessions keep theirs. Applied through every engine's existing
  // RuntimeMode mapping; it never changes a session's tool boundary, which
  // the room's board and the person's choice own.
  defaultRuntimeMode: RuntimeMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE)),
  ),
  // UP5: turn (wait) / paragraph (finished paragraphs) / token (token by token).
  responseStreamingMode: ResponseStreamingMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_RESPONSE_STREAMING_MODE)),
  ),
  // UP7: the override record — one row per (scope, key); see ScopedSettingOverride.
  scopedSettingOverrides: Schema.Array(ScopedSettingOverride).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  newWorktreesStartFromOrigin: Schema.Boolean.pipe(
    Schema.withDecodingDefault(Effect.succeed(true)),
  ),
  addProjectBaseDirectory: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  sourceControlWritingStyle: SourceControlWritingStyleSettings.pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),

  // Legacy single-instance-per-driver settings. Continues to be the source
  // of truth until `providerInstances` (below) lands per-driver migration
  // shims and the server starts hydrating instances from it. Driver-specific
  // schemas live here for the duration of the migration; once each driver
  // owns its config in its own package, this struct shrinks to nothing and
  // is removed entirely.
  providers: Schema.Struct({
    codex: CodexSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    claudeAgent: ClaudeSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    // "grok", "opencode" and "antigravity" left 2026-10-17 (the principal:
    // "Let's remove Antigravity, Grok, and OpenCode"); a stored settings file
    // that still names them decodes, the retired keys ignored.
  }).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  // New driver-agnostic instance map. Keyed by `ProviderInstanceId`; values
  // are `ProviderInstanceConfig` envelopes. The driver-specific config blob
  // is `Schema.Unknown` at this layer so envelopes with unknown drivers
  // (forks, downgrades, in-flight PR branches) round-trip without loss.
  // See providerInstance.ts for the forward/backward compatibility invariant.
  providerInstances: Schema.Record(ProviderInstanceId, ProviderInstanceConfig).pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  observability: ObservabilitySettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  // Device-local World → workspace bindings, keyed by worldKey. Patch
  // semantics are per-key set/overwrite (rebind); removal is the explicit
  // `worldWorkspaceBindingRemovals` patch operation, never silent key
  // omission.
  worldWorkspaceBindings: Schema.Record(TrimmedNonEmptyString, WorldWorkspaceBinding).pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
});
export type ServerSettings = typeof ServerSettings.Type;

export const DEFAULT_SERVER_SETTINGS: ServerSettings = Schema.decodeSync(ServerSettings)({});

export const ServerSettingsOperation = Schema.Literals([
  "normalize",
  "check-exists",
  "read-file",
  "read-secret",
  "remove-secret",
  "remove-stale-secret",
  "write-secret",
  "write-file",
  "prepare-directory",
]);
export type ServerSettingsOperation = typeof ServerSettingsOperation.Type;

export class ServerSettingsError extends Schema.TaggedErrorClass<ServerSettingsError>()(
  "ServerSettingsError",
  {
    settingsPath: Schema.String,
    operation: ServerSettingsOperation,
    providerInstanceId: Schema.optional(Schema.String),
    environmentVariable: Schema.optional(Schema.String),
    cause: Schema.Defect(),
  },
) {
  override get message(): string {
    const provider =
      this.providerInstanceId === undefined ? "" : ` for provider ${this.providerInstanceId}`;
    const variable =
      this.environmentVariable === undefined
        ? ""
        : ` and environment variable ${this.environmentVariable}`;
    return `Server settings ${this.operation} failed${provider}${variable} at ${this.settingsPath}.`;
  }
}

// ── Unified type ─────────────────────────────────────────────────────

export type UnifiedSettings = ServerSettings & ClientSettings;
export const DEFAULT_UNIFIED_SETTINGS: UnifiedSettings = {
  ...DEFAULT_SERVER_SETTINGS,
  ...DEFAULT_CLIENT_SETTINGS,
};

// ── Server Settings Patch (replace with a Schema.deepPartial if available) ──────────────────────────────────────────

const ModelSelectionPatch = Schema.Struct({
  instanceId: Schema.optionalKey(ProviderInstanceId),
  model: Schema.optionalKey(TrimmedNonEmptyString),
  options: Schema.optionalKey(ProviderOptionSelections),
});

const CodexSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  binaryPath: Schema.optionalKey(TrimmedString),
  homePath: Schema.optionalKey(TrimmedString),
  shadowHomePath: Schema.optionalKey(TrimmedString),
  launchArgs: Schema.optionalKey(TrimmedString),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
});

const ClaudeSettingsPatch = Schema.Struct({
  nativeAccount: Schema.optionalKey(ClaudeNativeAccountBinding),
  enabled: Schema.optionalKey(Schema.Boolean),
  binaryPath: Schema.optionalKey(TrimmedString),
  homePath: Schema.optionalKey(TrimmedString),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
  launchArgs: Schema.optionalKey(TrimmedString),
  autoCompactWindow: Schema.optionalKey(ClaudeAutoCompactWindow),
});

export const ServerSettingsPatch = Schema.Struct({
  // Server settings
  defaultSessionModel: Schema.optionalKey(Schema.NullOr(ModelSelection)),
  textGenerationModelSelection: Schema.optionalKey(Schema.NullOr(ModelSelection)),
  defaultSessionTools: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
  musicProvider: Schema.optionalKey(Schema.NullOr(MusicProviderId)),
  spotifyClientId: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
  enableProviderUpdateChecks: Schema.optionalKey(Schema.Boolean),
  telemetryEnabled: Schema.optionalKey(Schema.Boolean),
  backgroundActivity: Schema.optionalKey(
    Schema.Struct({
      schemaVersion: Schema.optionalKey(Schema.Literal(1)),
      profile: Schema.optionalKey(BackgroundActivityProfileSelection),
      baseProfile: Schema.optionalKey(BackgroundActivityProfile),
      overrides: Schema.optionalKey(BackgroundActivityOverrides),
    }),
  ),
  automaticGitFetchInterval: Schema.optionalKey(Schema.DurationFromMillis),
  providerHealthRefreshInterval: Schema.optionalKey(Schema.DurationFromMillis),
  backgroundActivityProfile: Schema.optionalKey(BackgroundActivityProfile),
  defaultThreadEnvMode: Schema.optionalKey(ThreadEnvMode),
  defaultRuntimeMode: Schema.optionalKey(RuntimeMode),
  responseStreamingMode: Schema.optionalKey(ResponseStreamingMode),
  // UP7: upsert by (scope, key). Never index-merged — a row replaces the row
  // with its identity and other rows stay.
  scopedSettingOverrides: Schema.optionalKey(Schema.Array(ScopedSettingOverride)),
  // UP7: reset is a named operation — each listed row is removed after the
  // upserts, so the value above it returns. A row both set and reset in one
  // patch ends up reset.
  scopedSettingOverrideResets: Schema.optionalKey(Schema.Array(ScopedSettingOverrideRef)),
  newWorktreesStartFromOrigin: Schema.optionalKey(Schema.Boolean),
  addProjectBaseDirectory: Schema.optionalKey(TrimmedString),
  sourceControlWritingStyle: Schema.optionalKey(
    Schema.Struct({
      mode: Schema.optionalKey(SourceControlWritingStyleMode),
      customInstructions: Schema.optionalKey(TrimmedString),
      followChangeRequestTemplates: Schema.optionalKey(Schema.Boolean),
    }),
  ),
  observability: Schema.optionalKey(
    Schema.Struct({
      otlpTracesUrl: Schema.optionalKey(TrimmedString),
      otlpMetricsUrl: Schema.optionalKey(TrimmedString),
    }),
  ),
  providers: Schema.optionalKey(
    Schema.Struct({
      codex: Schema.optionalKey(CodexSettingsPatch),
      claudeAgent: Schema.optionalKey(ClaudeSettingsPatch),
    }),
  ),
  // Whole-map replacement for the new instance config. Patching individual
  // entries is intentionally out of scope: the map is small, and partial
  // patches risk leaving driver-specific config in a half-merged state.
  // The web UI sends a fully-formed map every time it edits this field.
  providerInstances: Schema.optionalKey(Schema.Record(ProviderInstanceId, ProviderInstanceConfig)),
  // Per-key set/overwrite under the deep merge: sending one worldKey entry
  // binds or rebinds that World without touching other Worlds' bindings.
  worldWorkspaceBindings: Schema.optionalKey(
    Schema.Record(TrimmedNonEmptyString, WorldWorkspaceBinding),
  ),
  // The explicit binding removal the record's law demands: each listed
  // worldKey's whole row is deleted after the bindings merge, so removal is
  // always a named operation and never a silently omitted key. A worldKey
  // both patched and listed here ends up removed.
  worldWorkspaceBindingRemovals: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
  // Account removal is a named operation too (19 September 2026). A patched
  // `providerInstances` map upserts exactly the entries it names; an entry it
  // does not name is untouched. The list used to be replaced whole, so a save
  // erased every account the sending screen did not hold and a stale entry in
  // the map made the server refuse unrelated deletes. An id both patched and
  // listed here ends up removed.
  providerInstanceRemovals: Schema.optionalKey(Schema.Array(ProviderInstanceId)),
});
export type ServerSettingsPatch = typeof ServerSettingsPatch.Type;

export const ClientSettingsPatch = Schema.Struct({
  lightStudy: Schema.optionalKey(
    Schema.Literals(["none", "downlight", "pool", "rim", "wash", "ambient", "combined"]),
  ),
  lightIntensity: Schema.optionalKey(Schema.Literals(["whisper", "quiet", "present"])),
  lightBreathe: Schema.optionalKey(Schema.Boolean),
  hoverLight: Schema.optionalKey(Schema.Boolean),
  opaqueDoors: Schema.optionalKey(Schema.Boolean),
  doorFade: Schema.optionalKey(Schema.Boolean),
  cardGlow: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 100 }))),
  ambientMotion: Schema.optionalKey(Schema.Boolean),
  workingIndicator: Schema.optionalKey(WorkingIndicatorKind),
  workingIndicatorOrbLoop: Schema.optionalKey(WorkingIndicatorOrbLoop),
  workingIndicatorLoader: Schema.optionalKey(WorkingIndicatorLoader),
  confirmThreadArchive: Schema.optionalKey(Schema.Boolean),
  confirmThreadDelete: Schema.optionalKey(Schema.Boolean),
  diffIgnoreWhitespace: Schema.optionalKey(Schema.Boolean),
  density: Schema.optionalKey(ThemeDensity),
  contrast: Schema.optionalKey(ThemeContrast),
  environmentIdentificationMode: Schema.optionalKey(EnvironmentIdentificationMode),
  glassOpacity: Schema.optionalKey(GlassOpacity),
  shellGlass: Schema.optionalKey(ShellGlass),
  toneDownSections: Schema.optionalKey(Schema.Boolean),
  cleoPanelWide: Schema.optionalKey(Schema.Boolean),
  fontSizeInterface: Schema.optionalKey(InterfaceFontSize),
  fontSizePrompt: Schema.optionalKey(PromptFontSize),
  fontSizeCode: Schema.optionalKey(CodeFontSize),
  fontSizeTerminal: Schema.optionalKey(TerminalFontSize),
  fontFamilyCode: Schema.optionalKey(FontFamilyPreference),
  fontFamilyComposer: Schema.optionalKey(FontFamilyPreference),
  fontFamilySans: Schema.optionalKey(FontFamilyPreference),
  fontFamilyTerminal: Schema.optionalKey(FontFamilyPreference),
  fontSmoothing: Schema.optionalKey(Schema.Boolean),
  favorites: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        provider: ProviderInstanceId,
        model: TrimmedNonEmptyString,
      }),
    ),
  ),
  providerModelPreferences: Schema.optionalKey(
    Schema.Record(
      ProviderInstanceId,
      Schema.Struct({
        hiddenModels: Schema.Array(Schema.String).pipe(
          Schema.withDecodingDefault(Effect.succeed([])),
        ),
        modelOrder: Schema.Array(Schema.String).pipe(
          Schema.withDecodingDefault(Effect.succeed([])),
        ),
      }),
    ),
  ),
  planModeEnabled: Schema.optionalKey(Schema.Boolean),
  queueMessagesWhileWorking: Schema.optionalKey(Schema.Boolean),
  sidebarAutoSettleAfterDays: Schema.optionalKey(Schema.NullOr(SidebarAutoSettleAfterDays)),
  sidebarProjectGroupingMode: Schema.optionalKey(SidebarProjectGroupingMode),
  sidebarProjectGroupingOverrides: Schema.optionalKey(
    Schema.Record(TrimmedNonEmptyString, SidebarProjectGroupingMode),
  ),
  sidebarProjectSortOrder: Schema.optionalKey(SidebarProjectSortOrder),
  sidebarThreadSortOrder: Schema.optionalKey(SidebarThreadSortOrder),
  sessionNotifications: Schema.optionalKey(SessionNotificationMode),
  sessionNotificationsInApp: Schema.optionalKey(Schema.Boolean),
  sessionNotificationBadge: Schema.optionalKey(Schema.Boolean),
  sessionNotificationSound: Schema.optionalKey(SessionNotificationSound),
  timestampFormat: Schema.optionalKey(TimestampFormat),
  wordWrap: Schema.optionalKey(Schema.Boolean),
});
export type ClientSettingsPatch = typeof ClientSettingsPatch.Type;
